import axios, { AxiosError, AxiosResponse } from "axios";

import { includes, isEmpty, get as _get } from "lodash";

import { apiURL, basePath } from "@utils/Constants";
import { message as notificationMessage } from "antd";
import { strings } from "@utils/Strings";
import {
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
} from "@utils/getLocalStorage";

export const apiBaseUrl = `${apiURL}/${basePath}`;

const validateResponse = (
  res: AxiosResponse,
  resolve: Function,
  reject: Function
) => {
  const { data } = res;

  if ((data && data.status === "OK") || data.status === "CREATED") {
    resolve(data);
  } else {
    notificationMessage.error(data.message);
    reject(data.message);
  }
};

const extractErrMsg = (err: AxiosError, reject: Function) => {
  if (err) {
    const { message } = err.response?.data as any;
    if (message) {
      notificationMessage.error(message);
      reject(message);
    }
    notificationMessage.error(strings.someThingWentWrong);
    reject(strings.someThingWentWrong);
  }
};

const handleErr = (err: AxiosError, reject: Function) => {
  // if (err.response?.status === 401) {
  //   localStorage.removeItem("user");
  //   window.location.reload();
  // }
  return extractErrMsg(err, reject);
};

const api = axios.create({
  baseURL: apiBaseUrl,
});

let isRefreshing = false;
let refreshQueue: any = [];
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // if refresh token is already being used, add the original request to the queue
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshReq = await axios.post(
          `${apiBaseUrl}/userprofile/refresh/token`,
          {
            refreshToken: JSON.parse(localStorage.getItem("user") || "{}")
              ?.refreshToken,
          }
        );
        const { token, refreshToken } = _get(refreshReq, "data.response.data");
        let accessToken = token;
        if (token) {
          // udpate access and refresh token
          updateAccessToken(token, refreshToken);
          api.defaults.headers.common["Authorization"] = `Bearer ${token} `;
          // process the pending API calls
          refreshQueue.forEach((prom: any) => {
            prom.resolve(accessToken);
          });
          refreshQueue = [];
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          isRefreshing = false;
          return api(originalRequest);
        } else {
          logout();
        }
      } catch (error) {
        logout();
      } finally {
        isRefreshing = false;
        // refreshQueue = [];
      }
    }
    return Promise.reject(error);
  }
);

const logout = () => {
  window.localStorage.removeItem("user");
  window.localStorage.removeItem("onboardedCloudApps");
  window.location.reload();
};

const updateHeader = () => {
  const user = JSON.parse(localStorage.getItem("user") as any) || {};
  const authProvider = JSON.parse(localStorage.getItem("authProvider") as any);
  if (!isEmpty(user)) {
    const { token } = user;
    api.defaults.headers.common["Authorization"] = `Bearer ${token} `;
    api.defaults.headers.common["x_auth_provider"] = authProvider || "internal";
    return;
  }
  api.defaults.headers.common["Authorization"] = "";
};

export const get = async (endpoint: string) => {
  updateHeader();
  return new Promise((resolve, reject) => {
    api
      .get(endpoint)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

export const post = async (
  endpoint: string,
  // eslint-disable-next-line
  payload: any,
  headers?: { [key in string]: any }
) => {
  updateHeader();
  return new Promise((resolve, reject) => {
    api
      .post(endpoint, !isEmpty(payload) && payload, {
        ...(headers && {
          headers: {
            ...headers,
          },
        }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

// eslint-disable-next-line
export const put = async (endpoint: string, payload: any) => {
  updateHeader();
  return new Promise((resolve, reject) => {
    api
      .put(endpoint, payload)
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

// eslint-disable-next-line
export const remove = async (endpoint: string, payload?: any) => {
  updateHeader();
  return new Promise((resolve, reject) => {
    api
      .delete(endpoint, {
        ...(payload && { data: payload }),
      })
      .then((res: AxiosResponse) => {
        validateResponse(res, resolve, reject);
      })
      .catch((err: AxiosError) => {
        handleErr(err, reject);
      });
  });
};

export const upload = (endpoint: string, payload: any) => {
  return new Promise((resolve, reject) => {
    const user = JSON.parse(localStorage.getItem("user") as any) || {};
    if (!user) reject("Unauthorized User");
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${user.token}`);

    fetch(`${apiURL}/${basePath}/${endpoint}`, {
      method: "POST",
      body: payload,
      headers: myHeaders,
      redirect: "follow",
    })
      .then((response) => response.json())
      .then((res: any) => {
        resolve(res);
      })
      .catch((err: AxiosError) => {
        reject(handleErr(err, reject));
      });
  });
};
